import { toJQuery } from "@de-xima/xima-common-js-dom";
import {
  handleRejection,
  isEmpty,
  notNullish,
  notUndefined,
  parseRecord,
  parseString,
  removeStart,
  syncCallback,
} from "@de-xima/xima-common-js-lang";

import { getInstance as getFrameBusHandlerInstance } from "../core/FramebusHandler.ts";
import { ViewPushChannelListener } from "../core/PushChannelListener.ts";
import { loadingDialog } from "../core/Notification.ts";
import { getWidgetByVar, resolveURL, runRemoteCommand } from "../core/Util.ts";
import { DesignerFrameBusChildHandler, GenericDesignerFrameBusTopHandler } from "./DesignerFrameBus.ts";

interface IFormPreviewPageWidgetVars {
  debugDialog: string;
  pages: string;
  showAllElements: string;
}

interface IFormPreviewPageContainerIds {
  frameContainerId: string;
  submitDataTableId: string;
  openTools: string;
}

interface IFormPreviewPageControllerParams {
  frameId: string;
  pageId: string;
  containerIds: IFormPreviewPageContainerIds;
  socketChannel: string;
  widgetVars: IFormPreviewPageWidgetVars;
}

const PreviewWindowTarget = "preview";

const PreviewWindowFeatures = `toolbar=no, scrollbars=yes, resizable=yes, popup=true, height=${screen.height}, width=${screen.width}`;

const PreviewCss = `
.xm-form .XHeader .xm-item-div,
.xm-form .XPage .xm-item-div,
.xm-form .XFooter .xm-item-div {
  display: inline-table !important;
}`;

async function waitForReadyStateComplete(doc: Document): Promise<void> {
  if (doc.readyState === "complete") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    doc.addEventListener("readystatechange", () => {
      if (doc.readyState === "complete") {
        resolve(undefined);
      }
    });
  });
}

function getJqueryStatic(window: Window): JQueryStatic | undefined {
  const win = parseRecord(window);
  return typeof win.xm_jq === "function" ? (win.xm_jq() as JQueryStatic) : undefined;
}

function insertStyle(doc: Document, id: string, style: string): void {
  removeStyle(doc, id);
  const newStyle = doc.createElement("style");
  newStyle.id = id;
  doc.head.appendChild(newStyle);
  newStyle.textContent = style;
}

function removeStyle(doc: Document, id: string): void {
  const existingStyle = doc.getElementById(id);
  existingStyle?.parentElement?.removeChild(existingStyle);
}

async function clearFrame(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve) => {
    const listener = () => {
      resolve();
      frame.removeEventListener("load", listener);
    };
    frame.removeAttribute("src");
    frame.addEventListener("load", listener);
  });
}

class PreviewFrame {
  private readonly _id: string;

  private readonly _listenerOnLoad: Set<(event: Event) => void> = new Set();

  private _frame: HTMLIFrameElement | undefined;

  constructor(id: string) {
    this._id = id;
  }

  isHasForm(): boolean {
    return this.querySelectorAll(".xm-form").length > 0;
  }

  querySelectorAll(selector: string): HTMLElement[] {
    const document = this.getDoc();
    const HTMLElement = this.getGlobal("HTMLElement");
    if (document === undefined || HTMLElement === undefined) {
      return [];
    }
    const elements = document.querySelectorAll(selector);
    const result: HTMLElement[] = [];
    // document.querySelectorAll is not iterable in older browsers
    for (let i = 0; i < elements.length; i += 1) {
      const element = elements[i];
      if (element instanceof HTMLElement) {
        result.push(element);
      }
    }
    return result;
  }

  getFrame(): HTMLIFrameElement {
    if (this._frame === undefined || !document.contains(this._frame)) {
      const frame = document.getElementById(this._id);
      if (!(frame instanceof HTMLIFrameElement)) {
        throw new Error(`Cannot locate preview iframe with ID <${this._id}>`);
      }
      this._frame = frame;
      frame.addEventListener("load", (event) => this._invokeListenerOnLoad(event));
    }
    return this._frame;
  }

  getDoc(): Document | undefined {
    try {
      return this.getFrame().contentDocument ?? undefined;
    } catch {
      return undefined;
    }
  }

  getGlobal<K extends keyof typeof globalThis>(name: K): (typeof globalThis)[K] | undefined {
    try {
      const win = parseRecord(this.getWin());
      return win[name] as (typeof globalThis)[K] | undefined;
    } catch {
      return undefined;
    }
  }

  getJq(): JQueryStatic | undefined {
    const win = this.getWin();
    try {
      return win !== undefined ? getJqueryStatic(win) : undefined;
    } catch {
      return undefined;
    }
  }

  getWin(): Window | undefined {
    try {
      return this.getFrame().contentWindow ?? undefined;
    } catch {
      return undefined;
    }
  }

  async setHtml(html: string): Promise<void> {
    const frame = this.getFrame();
    frame.src = "";
    await clearFrame(frame);
    const doc = frame.contentDocument;
    if (notNullish(doc)) {
      doc.open();
      doc.write(html);
      doc.close();
    }
  }

  onLoad(listener: (event: Event) => void): void {
    this._listenerOnLoad.add(listener);
  }

  offLoad(listener: (event: Event) => void): void {
    this._listenerOnLoad.delete(listener);
  }

  private _invokeListenerOnLoad(event: Event) {
    for (const listener of this._listenerOnLoad) {
      try {
        listener(event);
      } catch (e) {
        console.error("Unhandled error in onload listener", e);
      }
    }
  }
}

class PreviewPageViewPushChannelListener extends ViewPushChannelListener {
  private readonly _controller: FormPreviewPageController;

  constructor(controller: FormPreviewPageController) {
    super();
    this._controller = controller;
    this._registerListeners();
  }

  private _registerListeners(): void {
    this.on("loadFormPreviewData", () => this._controller.loadFormPreview());
  }
}

class FormPreviewFrameBusHandler extends GenericDesignerFrameBusTopHandler {
  private readonly _controller: FormPreviewPageController;

  constructor(controller: FormPreviewPageController, pageId: string, socketChannel: string) {
    super(pageId, socketChannel);
    this._controller = controller;
    this._registerListeners();
  }

  private _registerListeners(): void {
    this.getFramebus().on("formPreview", () => this._controller.loadFormPreview());
  }
}

export async function openFormPreviewView(previewUrl: string): Promise<void> {
  const framebus = getFrameBusHandlerInstance();
  const absoluteUrl = resolveURL(previewUrl);
  if (framebus instanceof DesignerFrameBusChildHandler) {
    const win = framebus.openWindow(absoluteUrl, PreviewWindowTarget, PreviewWindowFeatures);
    if (win.window !== undefined) {
      win.window?.focus();
    }
    if (!win.new) {
      framebus.emitFormPreview();
    }
  }
}

export class FormPreviewPageController {
  private readonly _channelListener: PreviewPageViewPushChannelListener;
  private readonly _frameBusHandler: FormPreviewFrameBusHandler;
  private readonly _frame: PreviewFrame;
  private readonly _widgetVars: IFormPreviewPageWidgetVars;
  private readonly _onLoadListener: (event: Event) => void;
  private readonly _containerIds: IFormPreviewPageContainerIds;

  constructor(opts: IFormPreviewPageControllerParams) {
    this._widgetVars = opts.widgetVars;
    this._containerIds = opts.containerIds;
    this._frameBusHandler = new FormPreviewFrameBusHandler(this, opts.pageId, opts.socketChannel);
    this._channelListener = new PreviewPageViewPushChannelListener(this);
    this._frame = new PreviewFrame(opts.frameId);
    this._onLoadListener = (event) => this._onIframeLoad(event);
  }

  getFrameBusHandler(): FormPreviewFrameBusHandler {
    return this._frameBusHandler;
  }

  getChannelListener(): ViewPushChannelListener {
    return this._channelListener;
  }

  loadFormPreview(): void {
    handleRejection(this._doLoadFormPreview());
  }

  onShowAllElementsChange(): void {
    this._applyShowAllElements();
  }

  onToggleContainer(isSubmit: boolean): void {
    this._toggleContainer(isSubmit);
  }

  onPageChange(): void {
    this._applySelectedPages();
  }

  private _onIframeLoad(_event: Event): void {
    this._updateForIframeContent();
  }

  /**
   * Loads the preview HTML from the bean, and updates the iframe
   * with that HTML.
   */
  private async _doLoadFormPreview(): Promise<void> {
    try {
      loadingDialog(true);
      try {
        await this._channelListener.waitUntilConnected();
      } catch (e) {
        console.warn(
          "No web socket connection, loading form preview without web sockets, some features may not work",
          e,
        );
      }
      const response = await runRemoteCommand(BackendCommand?.Designer?.loadFormPreviewData, []);
      const window = this._frame.getWin();
      if (window !== undefined) {
        this._frame.offLoad(this._onLoadListener);
        await this._frame.setHtml(response.previewHtml ?? "");
        await waitForReadyStateComplete(window.document);
        this._updateForIframeContent();
        this._updateSelectedPages();
        this._applyShowAllElements();
        this._toggleContainer(false);
        this._registerFormPageChangeListener();
        this._frame.onLoad(this._onLoadListener);
      }
    } finally {
      loadingDialog(false);
    }
  }

  /**
   * Adds an event listener to the XIMA formcycle form to detect when
   * the user has navigated to a different form page.
   *
   * This is then used to select the corresponding page in the debug tools.
   *
   * The current implementation uses a resize observer to listen for size
   * changes of each page. An invisible page has a size of 0.
   */
  private _registerFormPageChangeListener(): void {
    const document = this._frame.getDoc();
    const ResizeObserver = this._frame.getGlobal("ResizeObserver");
    if (document !== undefined && ResizeObserver !== undefined) {
      const observer = new ResizeObserver(syncCallback(async () => this._updateSelectedPages()));
      for (const page of this._frame.querySelectorAll(".CXPage")) {
        observer.observe(page);
      }
    }
  }

  /**
   * Updates the UI for the content of the iframe.
   *
   * When the iframe contains a XIMA formcycle form, the preview tools are available.
   *
   * When the iframe does not contain a XIMA formcycle form (e.g. when the form was submitted),
   * the preview tools are not available.
   */
  private _updateForIframeContent(): void {
    if (!this._frame.isHasForm()) {
      getWidgetByVar(this._widgetVars.debugDialog, PrimeFaces.widget.Dialog)?.hide();
    }
  }

  /**
   * Finds the currently visible form page, and selects that page in the debug tools select menu.
   */
  private _updateSelectedPages(): void {
    const $ = this._frame.getJq();
    const widget = getWidgetByVar(this._widgetVars.pages, PrimeFaces.widget.SelectOneListbox);
    if (widget !== undefined && $ !== undefined) {
      const visiblePages = this._frame
        .querySelectorAll(".CXPage")
        .filter((page) => $(page).is(":visible"))
        .map((page) => page.dataset.xn);
      const pageName = visiblePages.length === 1 ? (visiblePages[0] ?? "") : "";
      const value = `page:${pageName}`;
      const item = widget.input
        .children("option")
        .toArray()
        .filter((option) => value === option.value)
        .map((option) => option.index)
        .map((index) => widget.items[index])
        .filter(notUndefined)[0];
      if (item !== undefined) {
        widget.unselectAll();
        widget.selectItem($(item));
      }
    }
  }

  /**
   * Reads the value of the "show all elements" checkbox in the debug tools, and
   * either shows all hidden element or restores the default visibility.
   */
  private _applyShowAllElements(): void {
    const widget = getWidgetByVar(this._widgetVars.showAllElements, PrimeFaces.widget.SelectBooleanCheckbox);
    const showElements = widget?.isChecked() ?? false;
    const document = this._frame.getDoc();
    if (document !== undefined) {
      if (showElements) {
        insertStyle(document, "xm-form-preview-inserted-style", PreviewCss);
      } else {
        removeStyle(document, "xm-form-preview-inserted-style");
      }
    }
  }

  /**
   * Reads the value of the "Form page" select menu in the debug tools, and
   * shows the corresponding page.
   */
  private _applySelectedPages(): void {
    const widget = getWidgetByVar(this._widgetVars.pages, PrimeFaces.widget.SelectOneListbox);
    const pageName = removeStart(parseString(widget?.input.val()), "page:");
    const $ = this._frame.getJq();
    if ($ !== undefined) {
      const allPages = $("[data-cn=XPage]");
      const requestedPages = allPages.filter((_idx, el) => isEmpty(pageName) || el.dataset.xn === pageName);
      // Prevent the user from being confused when we do not have any pages to display
      // May happen e.g. if a page was deleted?
      if (requestedPages.length > 0) {
        allPages.hide();
        requestedPages.show();
      }
    }
  }

  private _toggleContainer(isSubmit: boolean): void {
    const frameContainer = toJQuery(document.getElementById(this._containerIds.frameContainerId));
    const submitContainer = toJQuery(document.getElementById(this._containerIds.submitDataTableId));
    const tools = toJQuery(document.getElementById(this._containerIds.openTools));
    if (frameContainer && submitContainer) {
      if (isSubmit) {
        frameContainer.hide();
        submitContainer.show();
        tools.hide();
      } else {
        frameContainer.show();
        submitContainer.hide();
        tools.show();
      }
    }
  }
}
